import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { fetchGHData, type GHData, type GHContribution } from "./githubApi";
// @ts-ignore
import skillsData from "./three/skillData";
import "./Skills.css";

/* ── Filter categories ──
   A skill can belong to more than one: Java is a language and a backend tool,
   TypeScript is a language and a frontend one. `category` is its primary home
   (which branch it grows on); `also` lists the rest. */
const normaliseCat = (c: string) => {
  if (c === "Hosting" || c === "Cloud") return "Cloud";
  if (c === "CS" || c === "Tools") return "Languages";
  return c;
};

const catsOf = (s: any): string[] =>
  Array.from(new Set([s.category, ...(s.also || [])].map(normaliseCat)));

const FILTERS = ["All", ...Array.from(new Set(skillsData.flatMap((s: any) => catsOf(s))))];

const skillCategoryMap: Record<string, string[]> = {};
skillsData.forEach((s: any) => { skillCategoryMap[s.name] = catsOf(s); });

const skillMeta: Record<string, any> = {};
skillsData.forEach((s: any) => { skillMeta[s.name] = s; });

/* ── Houses chart data — Shaurya's skills ── */
const houses = [
  {
    house: "Gryffindor", crest: "\u{1F981}", trait: "Courage to face the user",
    category: "Frontend", cls: "gryf",
    skills: [
      { name: "React", level: 90 }, { name: "JavaScript", level: 92 },
      { name: "HTML5", level: 85 }, { name: "SASS", level: 80 },
      { name: "React Native", level: 85 }, { name: "Redux", level: 82 },
      { name: "TypeScript", level: 82 },
    ],
  },
  {
    house: "Slytherin", crest: "\u{1F40D}", trait: "Power behind the scenes",
    category: "Backend", cls: "slyth",
    skills: [
      { name: "Node.js", level: 82 }, { name: "Spring Boot", level: 75 },
      { name: "Express", level: 80 }, { name: "Kafka", level: 65 },
      { name: "REST", level: 85 }, { name: "Java", level: 80 },
      { name: "GraphQL", level: 75 },
    ],
  },
  {
    house: "Ravenclaw", crest: "\u{1F985}", trait: "Wisdom in every query",
    category: "Languages & CS", cls: "raven",
    skills: [
      { name: "C++", level: 95 }, { name: "DSA", level: 95 },
      { name: "Java", level: 80 }, { name: "TypeScript", level: 82 },
      { name: "Python", level: 70 }, { name: "System Design", level: 78 },
    ],
  },
  {
    house: "Hufflepuff", crest: "\u{1F9A1}", trait: "Reliable at every scale",
    category: "DevOps & Data", cls: "huffle",
    skills: [
      { name: "Git", level: 90 }, { name: "MongoDB", level: 80 },
      { name: "MySQL", level: 80 }, { name: "SQL", level: 82 },
      { name: "PostgreSQL", level: 78 }, { name: "Bash", level: 72 },
    ],
  },
  {
    house: "Dumbledore's Army", crest: "\u{26A1}", trait: "AI-powered development",
    category: "AI Tools", cls: "da",
    skills: [
      { name: "Claude", level: 88 }, { name: "ChatGPT", level: 85 },
      { name: "Gemini", level: 82 }, { name: "Flutter", level: 70 },
    ],
  },
];

/* ── Theme colors ── */
const THEME = {
  accent: "#ff8c00",
  highlight: "#e8dfd5",
  muted: "#c4a882",
};

/* ==================== CONTRIBUTION HEATMAP ==================== */

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_LABELS = ["","Mon","","Wed","","Fri",""];

const ContributionHeatmap: React.FC<{ contributions: GHContribution[]; total: number }> = ({ contributions, total }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const { cells, monthLabels, max } = useMemo(() => {
    const now = new Date();
    const calMap: Record<string, number> = {};
    for (const c of contributions) calMap[c.date] = c.count;
    const days: { x: number; y: number; count: number; date: string }[] = [];
    const months: { label: string; x: number }[] = [];
    const step = 15;
    let lastMonth = -1;
    for (let i = 364; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const key = d.toISOString().split("T")[0];
      const count = calMap[key] || 0;
      const week = Math.floor((364 - i) / 7);
      const dow = d.getDay();
      days.push({ x: week * step, y: dow * step, count, date: d.toLocaleDateString() });
      if (d.getMonth() !== lastMonth) { lastMonth = d.getMonth(); months.push({ label: MONTHS[d.getMonth()], x: week * step }); }
    }
    const counts = days.map(d => d.count).filter(c => c > 0);
    return { cells: days, monthLabels: months, max: Math.max(8, ...counts) };
  }, [contributions]);

  const W = 53 * 15;
  return (
    <HeatmapCard ref={ref} $visible={vis}>
      <HeatmapHeader>
        <HeatmapLeft><HeatmapTag>ACTIVITY</HeatmapTag><HeatmapTitle>GitHub Contributions</HeatmapTitle></HeatmapLeft>
        <HeatmapRight>&#x2299; <b>{total.toLocaleString()}</b> contributions in the last year</HeatmapRight>
      </HeatmapHeader>
      <HeatmapScroll>
        <HeatmapSvg viewBox={`-35 -5 ${W + 40} 130`}>
          {monthLabels.map((m, i) => (<text key={i} x={m.x} y={-1} fill="#4a5a4a" fontSize="9" fontFamily="JetBrains Mono">{m.label}</text>))}
          {DAY_LABELS.map((d, i) => (d ? <text key={i} x={-30} y={i * 15 + 18} fill="#4a5a4a" fontSize="8" fontFamily="JetBrains Mono" textAnchor="end">{d}</text> : null))}
          {cells.map((c, i) => (
            <circle key={i} cx={c.x + 5.5} cy={c.y + 11} r="5"
              fill={c.count === 0 ? "rgba(255,255,255,0.04)" : "#2cbb5d"}
              opacity={c.count === 0 ? 1 : Math.min(1, 0.25 + (c.count / max) * 0.75)}>
              <title>{c.date}: {c.count} contributions</title>
            </circle>
          ))}
          <text x={W - 100} y={120} fill="#4a5a4a" fontSize="8" fontFamily="JetBrains Mono">Less</text>
          {[0, 0.2, 0.4, 0.7, 1].map((t, i) => (
            <circle key={`l${i}`} cx={W - 65 + i * 14} cy={117} r="5" fill={t === 0 ? "rgba(255,255,255,0.04)" : "#2cbb5d"} opacity={t === 0 ? 1 : 0.25 + t * 0.75} />
          ))}
          <text x={W + 5} y={120} fill="#4a5a4a" fontSize="8" fontFamily="JetBrains Mono">More</text>
        </HeatmapSvg>
      </HeatmapScroll>
    </HeatmapCard>
  );
};

/* ==================== HOUSE CHART ==================== */

const HouseChart: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="sk-chart-section" ref={chartRef}>
      <div className="sk-chart-header">
        <span className="sk-overline" style={{ color: THEME.accent }}>
          The Sorting Hat Speaks
        </span>
        <h2 className="sk-chart-title" style={{ color: THEME.highlight }}>
          Skills by House
        </h2>
        <p className="sk-chart-sub" style={{ color: THEME.muted }}>
          Every skill has been sorted into the house that best represents its nature.
        </p>
      </div>

      <div className={`sk-chart ${visible ? "sk-chart-visible" : ""}`}>
        {houses.map((h, hi) => (
          <div
            className={`sk-chart-col sk-${h.cls}`}
            key={h.house}
            style={{ "--col-delay": `${hi * 0.15}s` } as React.CSSProperties}
          >
            <div className="sk-chart-parent">
              <span className="sk-chart-crest">{h.crest}</span>
              <h3 className="sk-chart-house">{h.house}</h3>
              <p className="sk-chart-trait">{h.trait}</p>
              <span className="sk-chart-cat">{h.category}</span>
              <span className="sk-chart-count">{h.skills.length} spells</span>
            </div>

            <div className="sk-chart-line">
              <div className="sk-chart-line-glow" />
            </div>

            <div className="sk-chart-children">
              {h.skills.map((s, si) => (
                <div
                  key={s.name}
                  className="sk-chart-child"
                  style={{ "--child-delay": `${hi * 0.15 + si * 0.08}s` } as React.CSSProperties}
                >
                  <div className="sk-chart-child-top">
                    <span className="sk-chart-skill-name">{s.name}</span>
                    <span className="sk-chart-skill-lvl">{s.level}%</span>
                  </div>
                  <div className="sk-chart-bar">
                    <div
                      className="sk-chart-bar-fill"
                      style={{ width: visible ? `${s.level}%` : "0%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ==================== WEBGL CHECK ==================== */

function hasWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return !!(canvas.getContext("webgl") || canvas.getContext("webgl2"));
  } catch (e) {
    return false;
  }
}

/* ==================== MAIN COMPONENT ==================== */

const Skills: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<any>(null);

  const [labels, setLabels] = useState<any[]>([]);
  const [hovered, setHovered] = useState<string | null>(null);
  const [webglOk] = useState(hasWebGL);
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [ghData, setGhData] = useState<GHData | null>(null);

  const onLabels = useCallback((newLabels: any[]) => {
    setLabels(newLabels);
  }, []);

  useEffect(() => {
    if (!webglOk || !containerRef.current) return;

    let scene: any = null;
    let disposed = false;

    import("./three/BanyanScene").then(({ default: BanyanScene }) => {
      if (disposed) return;
      scene = new BanyanScene(containerRef.current, { onLabels });
      sceneRef.current = scene;
    });

    return () => {
      disposed = true;
      if (scene) scene.dispose();
      sceneRef.current = null;
    };
  }, [webglOk, onLabels]);

  useEffect(() => { fetchGHData().then(setGhData); }, []);

  return (
    <div className="sk-page">
      {webglOk ? (
        <div className="sk-scene-wrap">
          <div ref={containerRef} className="sk-scene-container" />

          {/* Vertical text flanking the tree */}
          <div className="sk-vert-text sk-vert-left" style={{ color: THEME.highlight }}>
            <span className="sk-vert-word sk-fade-left" style={{ animationDelay: "0s" }}>Every</span>
            <span className="sk-vert-word sk-fade-left" style={{ animationDelay: "0.1s" }}>Branch</span>
            <span className="sk-vert-word sk-fade-left" style={{ animationDelay: "0.2s" }}>Tells</span>
            <span className="sk-vert-word sk-fade-left" style={{ animationDelay: "0.3s" }}>a</span>
            <span className="sk-vert-word sk-fade-left" style={{ animationDelay: "0.4s", color: THEME.accent }}>Story</span>
          </div>
          <div className="sk-lyric-text sk-vert-right" style={{ color: THEME.muted }}>
            {["Each", "glowing", "leaf", "is", "a", "skill", "cultivated", "across", "years"].map((word, i) => (
              <span
                key={word + i}
                className="sk-lyric-word sk-fade-bottom"
                style={{
                  animationDelay: `${0.8 + i * 0.35}s`,
                  ...(word === "cultivated" || word === "years" ? { color: THEME.accent } : {}),
                }}
              >
                {word}
              </span>
            ))}
          </div>

          {/* Category filter buttons */}
          <div className="sk-filter-bar">
            {FILTERS.map(f => (
              <button
                key={f as string}
                className={`sk-filter-btn ${activeFilter === f ? "sk-filter-active" : ""}`}
                style={activeFilter === f ? { borderColor: THEME.accent, color: THEME.accent } : {}}
                onClick={() => { setActiveFilter(f as string); setSelectedSkill(null); }}
              >
                {f as string}
              </button>
            ))}
          </div>

          {/* Skill orbs with icon + name */}
          <div className="sk-labels-layer">
            {labels.map((label) => {
              const cats = skillCategoryMap[label.name] || [];
              const dimmed = activeFilter !== "All" && !cats.includes(activeFilter);
              const isSelected = selectedSkill === label.name;
              return (
                <div
                  key={label.name}
                  className={`sk-label ${hovered === label.name ? "sk-label-active" : ""} ${dimmed ? "sk-label-dimmed" : ""} ${isSelected ? "sk-label-selected" : ""}`}
                  style={{
                    left: label.x,
                    top: label.y,
                    "--lbl-color": label.color,
                  } as React.CSSProperties}
                  onMouseEnter={() => setHovered(label.name)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setSelectedSkill(isSelected ? null : label.name)}
                >
                  <div className="sk-label-orb">
                    <span
                      className="iconify sk-label-icon"
                      data-icon={label.iconify}
                      data-inline="false"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <span className="sk-label-name">{label.name}</span>
                </div>
              );
            })}
          </div>

          {/* Skill detail card */}
          {selectedSkill && skillMeta[selectedSkill] && (
            <div className="sk-card-backdrop" onClick={() => setSelectedSkill(null)}>
              <div className="sk-skill-card" onClick={e => e.stopPropagation()}>
                <button className="sk-card-close" onClick={() => setSelectedSkill(null)}>&times;</button>
                <div className="sk-card-icon-wrap" style={{ "--card-color": skillMeta[selectedSkill].color } as React.CSSProperties}>
                  <span
                    className="iconify sk-card-icon"
                    data-icon={skillMeta[selectedSkill].iconify}
                    data-inline="false"
                  />
                </div>
                <h3 className="sk-card-name">{selectedSkill}</h3>
                <span className="sk-card-category">{(skillCategoryMap[selectedSkill] || []).join(" · ")}</span>
                <div className="sk-card-level">
                  <div className="sk-card-level-track">
                    <div
                      className="sk-card-level-fill"
                      style={{
                        width: `${skillMeta[selectedSkill].level}%`,
                        background: skillMeta[selectedSkill].color,
                      }}
                    />
                  </div>
                  <span className="sk-card-level-num" style={{ color: skillMeta[selectedSkill].color }}>
                    {skillMeta[selectedSkill].level}%
                  </span>
                </div>
                <div className="sk-card-rank">
                  {skillMeta[selectedSkill].level >= 90 ? "Mastery" :
                   skillMeta[selectedSkill].level >= 80 ? "Advanced" :
                   skillMeta[selectedSkill].level >= 60 ? "Proficient" : "Learning"}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="sk-fallback">
          <p>Your browser doesn't support WebGL. Here are my skills:</p>
          <div className="sk-fallback-grid">
            {[
              "React", "JavaScript", "C++", "Java", "Node.js", "Spring Boot",
              "HTML5", "TypeScript", "Python", "Git", "MongoDB", "MySQL",
              "SQL", "Express", "DSA", "Redux", "SASS", "React Native",
              "Kafka", "REST", "GraphQL", "PostgreSQL", "Flutter", "System Design",
              "Bash", "ESLint", "Claude", "ChatGPT", "Gemini",
            ].map((name) => (
              <span key={name} className="sk-fallback-tag">{name}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── Hogwarts Skill Chart ── */}
      <HouseChart />

      {/* GitHub heatmap + Open Source PRs moved to /opensource */}
    </div>
  );
};

/* MaraudersMap moved to /opensource page */

export default Skills;

/* ==================== STYLED COMPONENTS ==================== */

const GithubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 2rem 4rem;
`;

const HeatmapCard = styled.div<{ $visible: boolean }>`
  width: 100%;
  max-width: 850px;
  padding: 2rem;
  background: rgba(10,14,10,0.6);
  border: 1px solid rgba(44,187,93,0.08);
  border-radius: 16px;
  margin-bottom: 3rem;
  opacity: ${({$visible}) => $visible ? 1 : 0};
  transform: translateY(${({$visible}) => $visible ? 0 : "25px"});
  transition: opacity 0.7s ease, transform 0.7s ease;
`;

const HeatmapHeader = styled.div`display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:1rem;margin-bottom:1.5rem;`;
const HeatmapLeft = styled.div``;
const HeatmapTag = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.55rem;color:#4a6a4a;letter-spacing:0.25em;margin-bottom:0.3rem;`;
const HeatmapTitle = styled.h3`font-family:"Cinzel",serif;font-size:1.3rem;font-weight:700;color:#e0e8e0;`;
const HeatmapRight = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.6rem;color:#5a7a5a;& b{color:#c0e0c0;font-size:0.85rem;}`;
const HeatmapScroll = styled.div`overflow-x:auto;overflow-y:hidden;-ms-overflow-style:none;scrollbar-width:none;&::-webkit-scrollbar{display:none;}`;
const HeatmapSvg = styled.svg`width:100%;min-width:700px;height:auto;display:block;`;

/* ══════════════════════════════════════════════════════════
   MARAUDER'S MAP — Parchment-style open source section
   ══════════════════════════════════════════════════════════ */

/* Map styles moved to OpenSource component */
